import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Apple, Smartphone, RefreshCw, RotateCcw, AlertCircle, CheckCircle2, Eye, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fetchAuth = (path, opts = {}) => {
  const token = localStorage.getItem('cca_token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
};

const StatusBadge = ({ processed, applied, action }) => {
  if (!processed) return <Badge variant="outline" className="border-amber-500 text-amber-600">pending</Badge>;
  if (applied) {
    const tone = action === 'grant' ? 'bg-emerald-100 text-emerald-700' :
                 action === 'revoke' ? 'bg-red-100 text-red-700' :
                 'bg-slate-100 text-slate-600';
    return <Badge className={tone}>{action}</Badge>;
  }
  return <Badge variant="outline" className="text-slate-500">no-op</Badge>;
};

const EventTable = ({ provider, events, onView, onReprocess, onGoogleVerify, busy }) => {
  if (!events || events.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500 text-sm">
        No {provider} events yet. Send a test notification from {provider === 'apple' ? 'App Store Connect' : 'Play Console RTDN'} to verify the webhook is wired.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Received</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((e) => (
            <TableRow key={e.event_id} data-testid={`iap-event-row-${e.event_id}`}>
              <TableCell className="text-xs whitespace-nowrap">
                {new Date(e.received_at).toLocaleString()}
              </TableCell>
              <TableCell className="text-xs font-mono">{e.type}</TableCell>
              <TableCell className="text-xs">
                {e.user_id ? (
                  <span className="font-mono text-slate-700">{e.user_id.slice(0, 12)}…</span>
                ) : (
                  <span className="text-slate-400">unlinked</span>
                )}
              </TableCell>
              <TableCell>
                <StatusBadge processed={e.processed} applied={e.applied} action={e.action} />
              </TableCell>
              <TableCell className="text-right space-x-1">
                <Button size="sm" variant="ghost" onClick={() => onView(e.event_id)} data-testid={`iap-view-${e.event_id}`} title="View payload">
                  <Eye className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onReprocess(e.event_id)} disabled={busy === e.event_id} data-testid={`iap-reprocess-${e.event_id}`} title="Re-run entitlement logic">
                  <RotateCcw className={`h-3 w-3 ${busy === e.event_id ? 'animate-spin' : ''}`} />
                </Button>
                {provider === 'google' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onGoogleVerify(e.event_id)}
                    disabled={busy === `verify-${e.event_id}`}
                    data-testid={`iap-google-verify-${e.event_id}`}
                    title="Ask Google Play for the current subscription state"
                  >
                    <ShieldCheck className={`h-3 w-3 ${busy === `verify-${e.event_id}` ? 'animate-spin' : ''}`} />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default function IapEventsTab() {
  const [appleEvents, setAppleEvents] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, g] = await Promise.all([
        fetchAuth('/api/iap/events?provider=apple&limit=100'),
        fetchAuth('/api/iap/events?provider=google&limit=100'),
      ]);
      if (a.ok) setAppleEvents(await a.json());
      if (g.ok) setGoogleEvents(await g.json());
    } catch (e) {
      toast.error('Failed to load IAP events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const view = async (eventId) => {
    try {
      const r = await fetchAuth(`/api/iap/events/${eventId}`);
      if (r.ok) setViewing(await r.json());
      else toast.error('Failed to fetch event');
    } catch (e) {
      toast.error(String(e));
    }
  };

  const reprocess = async (eventId) => {
    setBusyId(eventId);
    try {
      const r = await fetchAuth(`/api/iap/events/${eventId}/reprocess`, { method: 'POST' });
      const j = await r.json();
      if (r.ok) {
        toast.success(`Reprocessed: action=${j.action || 'none'}, applied=${j.applied}`);
        load();
      } else {
        toast.error(j.detail || 'Reprocess failed');
      }
    } catch (e) {
      toast.error(String(e));
    } finally {
      setBusyId(null);
    }
  };

  const googleVerify = async (eventId) => {
    setBusyId(`verify-${eventId}`);
    try {
      const r = await fetchAuth(`/api/iap/events/${eventId}/google-verify`, { method: 'POST' });
      const j = await r.json();
      if (r.ok) {
        const v = j.verify || {};
        if (v.skipped) {
          toast.warning(`Skipped: ${v.reason || 'no service account configured'}`);
        } else if (v.verified === true) {
          toast.success(`Play says ACTIVE · state=${v.state}${v.expiry_time ? ` · expires ${new Date(v.expiry_time).toLocaleDateString()}` : ''}`);
        } else if (v.verified === false) {
          toast.error(`Play says INACTIVE · state=${v.state || 'unknown'}${v.error ? ` · ${v.error}` : ''}`);
        } else {
          toast.info(`Play response: ${JSON.stringify(v).slice(0, 200)}`);
        }
        setViewing({ ...j, event_id: eventId, _manual_verify: true });
      } else {
        toast.error(j.detail || 'Verify failed');
      }
    } catch (e) {
      toast.error(String(e));
    } finally {
      setBusyId(null);
    }
  };

  const appleUnlinked = appleEvents.filter((e) => !e.user_id).length;
  const googleUnlinked = googleEvents.filter((e) => !e.user_id).length;

  return (
    <div className="space-y-6" data-testid="iap-events-tab">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-[#00E5CC]" />
              IAP Events
            </CardTitle>
            <CardDescription>
              Live stream from Apple App Store Server Notifications V2 + Google Play Real-time Developer Notifications.
              Click <RotateCcw className="inline h-3 w-3" /> to re-run entitlement logic on any event.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="iap-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-2 text-xs text-slate-500">
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant="outline"><CheckCircle2 className="h-3 w-3 mr-1 text-emerald-600" /> Health: <a href={`${API}/api/iap/health`} target="_blank" rel="noreferrer" className="ml-1 underline">/api/iap/health</a></Badge>
            {(appleUnlinked + googleUnlinked) > 0 && (
              <Badge variant="outline" className="border-amber-500 text-amber-700">
                {appleUnlinked + googleUnlinked} unlinked event{(appleUnlinked + googleUnlinked) === 1 ? '' : 's'}
                <span className="ml-1 text-slate-500">(no user_id — the buyer hasn't called /link yet)</span>
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Apple className="h-4 w-4" /> Apple ({appleEvents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EventTable provider="apple" events={appleEvents} onView={view} onReprocess={reprocess} onGoogleVerify={googleVerify} busy={busyId} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Smartphone className="h-4 w-4" /> Google ({googleEvents.length})
            <span className="text-xs text-muted-foreground font-normal ml-2 inline-flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> per-row icon = ask Play API
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <EventTable provider="google" events={googleEvents} onView={view} onReprocess={reprocess} onGoogleVerify={googleVerify} busy={busyId} />
        </CardContent>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="iap-event-detail">
          <DialogHeader>
            <DialogTitle>Event {viewing?.event_id}</DialogTitle>
            <DialogDescription>
              {viewing?.provider} · {viewing?.type} · received {viewing && new Date(viewing.received_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto whitespace-pre-wrap break-all">
{viewing ? JSON.stringify(viewing, null, 2) : ''}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
